import { ref } from 'vue'
import { FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useToast } from './useToast'

export interface OpdrachtgeverShare {
  opdrachtgever_id: string
  naam: string | null
  email: string
  is_eigenaar: boolean
  is_beheerder: boolean
  offerte_gedeeld: boolean
  profile_completed: boolean
  created_at: string
}

export interface InstallateurShare {
  installateur_id: string
  naam: string
  bedrijfsnaam: string | null
  email: string | null
  has_account: boolean
  profile_completed: boolean
  created_at: string
}

export interface RapportShares {
  opdrachtgevers: OpdrachtgeverShare[]
  installateurs: InstallateurShare[]
}

export interface ShareRapportPayload {
  shareType: 'opdrachtgever' | 'installateur'
  email: string
  isEigenaar?: boolean
  isBeheerder?: boolean
  includeOfferte?: boolean
  naam?: string
  resend?: boolean
}

export interface ShareRapportResult {
  success: boolean
  share_type: 'opdrachtgever' | 'installateur'
  email: string
  user_created: boolean
  already_shared: boolean
  email_sent: boolean
  opdrachtgever_id?: string
  installateur_id?: string
}

/**
 * Sharing a rapport with other opdrachtgevers (rapporten_opdrachtgevers) and
 * installateurs (rapporten_installateurs) from the portal. Listing goes through
 * the get_rapport_shares RPC because portal users cannot read other users'
 * e-mail addresses under RLS; sharing goes through the share-rapport edge
 * function because it may need to create the recipient's account.
 */
export function useRapportShares(rapportId: string) {
  const toast = useToast()
  const shares = ref<RapportShares>({ opdrachtgevers: [], installateurs: [] })
  const loading = ref(false)
  const sharing = ref(false)

  const fetchShares = async () => {
    loading.value = true
    try {
      const { data, error } = await supabase.rpc('get_rapport_shares', { p_rapport_id: rapportId })
      if (error) throw error
      const result = (data ?? { opdrachtgevers: [], installateurs: [] }) as unknown as RapportShares
      shares.value = {
        opdrachtgevers: result.opdrachtgevers ?? [],
        installateurs: result.installateurs ?? [],
      }
    } catch (error) {
      console.error('Error fetching rapport shares:', error)
      toast.error('Fout bij ophalen van gedeelde toegang', error)
    } finally {
      loading.value = false
    }
  }

  const extractFunctionError = async (error: unknown): Promise<string> => {
    if (error instanceof FunctionsHttpError) {
      try {
        const body = await error.context.json()
        if (body?.error) return String(body.error)
      } catch {
        // fall through to the generic message
      }
    }
    if (error instanceof FunctionsFetchError) {
      return 'Delen van het rapport is mislukt. Controleer uw internetverbinding en probeer het opnieuw.'
    }
    return 'Delen van het rapport is mislukt'
  }

  // The browser -> /functions/ fetch intermittently rejects on mobile networks
  // (FunctionsFetchError, Sentry PORTAL-VUE-5), so retry transient network
  // failures a couple of times before giving up.
  const invokeShareRapport = async (body: Record<string, unknown>) => {
    const delays = [500, 1500]
    for (let attempt = 0; ; attempt++) {
      const { data, error } = await supabase.functions.invoke('share-rapport', { body })
      if (!error) return data
      if (!(error instanceof FunctionsFetchError) || attempt >= delays.length) throw error
      await new Promise((resolve) => setTimeout(resolve, delays[attempt]))
    }
  }

  const shareRapport = async (payload: ShareRapportPayload): Promise<ShareRapportResult | null> => {
    sharing.value = true
    try {
      const data = await invokeShareRapport({
        rapport_id: rapportId,
        share_type: payload.shareType,
        email: payload.email,
        is_eigenaar: payload.isEigenaar,
        is_beheerder: payload.isBeheerder,
        include_offerte: payload.includeOfferte,
        naam: payload.naam,
        resend: payload.resend,
      })

      const result = data as ShareRapportResult
      if (result.already_shared && !payload.resend) {
        toast.info(`${result.email} heeft al toegang tot dit rapport`)
      } else if (!result.email_sent) {
        toast.error(
          `${result.email} heeft toegang, maar de uitnodiging kon niet worden verstuurd. Probeer het opnieuw.`,
          new Error(`share-rapport: invite email not sent to ${result.email}`),
        )
      } else if (result.user_created) {
        toast.success(`Uitnodiging verstuurd naar ${result.email}`)
      } else if (payload.resend) {
        toast.success(`Uitnodiging opnieuw verstuurd naar ${result.email}`)
      } else {
        toast.success(`Rapport gedeeld met ${result.email}`)
      }
      await fetchShares()
      return result
    } catch (error) {
      console.error('Error sharing rapport:', error)
      toast.error(await extractFunctionError(error), error)
      return null
    } finally {
      sharing.value = false
    }
  }

  const updateOpdrachtgeverRoles = async (
    opdrachtgeverId: string,
    roles: { is_eigenaar: boolean; is_beheerder: boolean },
  ) => {
    try {
      const { error } = await supabase
        .from('rapporten_opdrachtgevers')
        .update(roles)
        .eq('rapport_id', rapportId)
        .eq('opdrachtgever_id', opdrachtgeverId)
      if (error) throw error
      toast.success('Rollen bijgewerkt')
      await fetchShares()
    } catch (error) {
      console.error('Error updating share roles:', error)
      toast.error('Fout bij bijwerken van rollen', error)
    }
  }

  const revokeOpdrachtgever = async (opdrachtgeverId: string) => {
    try {
      const { error } = await supabase.rpc('revoke_rapport_share', {
        p_rapport_id: rapportId,
        p_opdrachtgever_id: opdrachtgeverId,
      })
      if (error) throw error
      toast.success('Toegang ingetrokken')
      await fetchShares()
    } catch (error) {
      console.error('Error revoking opdrachtgever share:', error)
      toast.error('Fout bij intrekken van toegang', error)
    }
  }

  const revokeInstallateur = async (installateurId: string) => {
    try {
      const { error } = await supabase
        .from('rapporten_installateurs')
        .delete()
        .eq('rapport_id', rapportId)
        .eq('installateur_id', installateurId)
      if (error) throw error
      toast.success('Toegang ingetrokken')
      await fetchShares()
    } catch (error) {
      console.error('Error revoking installateur share:', error)
      toast.error('Fout bij intrekken van toegang', error)
    }
  }

  return {
    shares,
    loading,
    sharing,
    fetchShares,
    shareRapport,
    updateOpdrachtgeverRoles,
    revokeOpdrachtgever,
    revokeInstallateur,
  }
}
